import { useEffect, useState, useTransition } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { generatePath, useSearchParams } from 'react-router-dom';
import { useDebounce } from 'react-use';
import styled from 'styled-components';

import { useTypedParams } from '@sorare/routing';

import { SearchInput } from '@sorare/core/src/atoms/inputs/SearchInput';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { TabBar } from '@sorare/core/src/atoms/navigation/TabBar';
import { Breadcrumb } from '@sorare/core/src/components/breadcrumb';
import {
  BLOG,
  BLOG_SPORT,
} from '@sorare/core/src/constants/__generated__/routes';
import { useSeoContext } from '@sorare/core/src/contexts/seo';
import { glossary } from '@sorare/core/src/lib/glossary';

import type { CategoryVariables } from 'components/BlogResults';
import { BlogResults } from 'components/BlogResults';
import { FootballCategories, MlbNbaCategories } from 'components/Tabs';

import type { PageParams } from './__generated__/routeParams';

const SearchWrapper = styled.div`
  max-width: fit-content;
`;

const messages = defineMessages({
  seoTitle: {
    id: 'blog.seo.title',
    defaultMessage: 'Fantasy Sports Reimagined - Sorare Blog',
  },
  seoDescription: {
    id: 'blog.seo.description',
    defaultMessage:
      'Experience football, basketball, and baseball like never before. Collect. Play. Win. #OwnYourGame.',
  },
});

const HomeByCategory = () => {
  const { formatMessage } = useIntl();
  const { setPageMetadata } = useSeoContext();
  const { sport, category } = useTypedParams<PageParams>();
  const sportCategory = sport?.toLowerCase();
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [, setSearchParams] = useSearchParams();
  const [isPending, startTransition] = useTransition();

  useDebounce(
    () => {
      if (search !== debouncedSearch) {
        startTransition(() => {
          setDebouncedSearch(search);
        });
      }
    },
    300,
    [search]
  );

  useEffect(() => {
    if (debouncedSearch) {
      setSearchParams(`search=${debouncedSearch}`);
    } else {
      setSearchParams();
    }
  }, [debouncedSearch, setSearchParams]);

  let footballCategoryVariable;
  switch (category) {
    case 'fantasy-meets-reality':
      footballCategoryVariable = [
        {
          slug: 'fantasy-meets-reality',
          databaseId: '1515',
        },
      ];
      break;
    case 'get-started':
      footballCategoryVariable = [{ slug: 'get-started', databaseId: '1500' }];
      break;
    default:
      footballCategoryVariable = [{ slug: 'news', databaseId: '1505' }];
  }

  const MlbCategoriesVariables =
    category === 'news'
      ? [
          { slug: 'gameplay-updates-mlb', databaseId: '1501' },
          { slug: 'mlb-scouting', databaseId: '1509' },
          { slug: 'nba-special-events', databaseId: '1513' },
        ]
      : [{ slug: 'mlb-community-stories', databaseId: '1507' }];

  const NbaCategoriesVariables =
    category === 'news'
      ? [
          { slug: 'gameplay-updates-nba', databaseId: '1502' },
          { slug: 'scouting', databaseId: '1499' },
          { slug: 'nba-special-events', databaseId: '1513' },
        ]
      : [{ slug: 'nba-community-stories', databaseId: '1512' }];

  let categoriesVariables: CategoryVariables;
  switch (sportCategory) {
    case 'nba':
      categoriesVariables = NbaCategoriesVariables;
      break;
    case 'mlb':
      categoriesVariables = MlbCategoriesVariables;
      break;
    default:
      categoriesVariables = footballCategoryVariable;
  }

  useEffect(() => {
    setPageMetadata(formatMessage(messages.seoTitle), {
      description: formatMessage(messages.seoDescription),
    });
  }, [formatMessage, setPageMetadata]);

  return (
    <Vertical gap={4}>
      <Breadcrumb
        sections={[
          { name: 'Blog', to: BLOG },
          {
            name: sportCategory ?? '',
            to: generatePath(BLOG_SPORT, {
              sport: sportCategory ?? '',
            }),
          },
        ]}
      />
      <TabBar variant="primary">
        {sportCategory === 'football' ? (
          <FootballCategories sportCategory={sportCategory} />
        ) : (
          <MlbNbaCategories sportCategory={sportCategory ?? 'nba'} />
        )}
      </TabBar>
      <SearchWrapper>
        <SearchInput
          withIcon
          placeholder={formatMessage(glossary.search)}
          value={search || ''}
          onChange={event =>
            startTransition(() => {
              setSearch(event.target.value);
            })
          }
        />
      </SearchWrapper>
      <BlogResults
        search={debouncedSearch}
        categoryVariables={categoriesVariables}
        isSearching={!!isPending}
      />
    </Vertical>
  );
};
export default HomeByCategory;
